import _ from 'lodash';
import { Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { DialogService } from 'app/dialog/services/dialog.service';
import { selectDialogState } from 'app/dialog/features/dialog.selectors';
import { DomSanitizer } from '@angular/platform-browser';
import { CropperComponent as AngularCropperComponent, AngularCropperjsModule } from 'angular-cropperjs';
import { ImageService } from 'app/core/services/image.service';
import { filter } from 'rxjs/operators';
import { LoadingService } from '../../../services/loading.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FileLoadingComponent } from '../../file-loading/file-loading.component';

@Component({
  standalone:true,
  imports:[CommonModule, FileLoadingComponent,AngularCropperjsModule],
  selector: 'app-cropper',
  templateUrl: './cropper.component.html',
  styleUrls: ['./cropper.component.scss']
})
export class CropperComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));

  @ViewChild('cropper') public image: AngularCropperComponent;

  public cropperOptions = {
    aspectRatio: 2/1,
    movable: true,
    scalable: true,
    zoomable: true,
    viewMode: 0,
    checkCrossOrigin: false,
    autoCropArea: 1,
    ready: () => this.onCropperReady()
  };

  public imagesQueue = [];
  public isMultyImages = false;
  public dialog;
  public currentImageUrl;
  public currentImage;
  public imageId = 'option-image';
  public isLoading = false;

  constructor(
    private store: Store,
    private dialogService: DialogService,
    private imageService: ImageService,
    private loadingService: LoadingService,
    public domSanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.loadingService.enabled = false;

    this.dialog$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe( dialog => {
      this.dialog = dialog

      if (dialog.initialState?.images) {
        this.isMultyImages  = true
        this.imagesQueue = dialog.initialState.images;
        this.imageId = dialog.initialState.id
        this.onCreateImageUrl();
      }
    });

    this.imageService.imageSubject.pipe(
      filter(({ isMulti }) => isMulti === true),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (!this.imagesQueue.length) {
        this.dialogService.closeDialogSubject.next(null);
      }

      this.isLoading = false;

      this.onCreateImageUrl();
    });
  }


  ngOnDestroy(): void {
    this.dialogService.closeDialogSubject.next(null);
  }

  // Calculate and set correct image and crop box sizes
  onCropperReady() {
    if (!this.image || !this.image.cropper) {
      return;
    }

    const cropper = this.image.cropper;
    const canvasData = cropper.getCanvasData();
    const margin = 20; // 20px margin from edges

    const wrapBox = document.querySelector('.cropper-wrap-box') as HTMLElement;
    if (!wrapBox) {
      return;
    }

    const wrapBoxWidth = wrapBox.clientWidth;
    const wrapBoxHeight = wrapBox.clientHeight;

    let newWidth = canvasData.width;
    let newHeight = canvasData.height;

    // Shrink if initial image size is not fitting the container including margin
    if (newWidth + margin * 2 > wrapBoxWidth || newHeight + margin * 2 > wrapBoxHeight) {
        const widthRatio = (wrapBoxWidth - margin * 2) / newWidth;
        const heightRatio = (wrapBoxHeight - margin * 2) / newHeight;
        const scaleRatio = Math.min(widthRatio, heightRatio, 1);

        newWidth *= scaleRatio;
        newHeight *= scaleRatio;

        cropper.setCanvasData({
            width: newWidth,
            height: newHeight
        });
    }

    // Calculate and set image size based on which side is bigger
    let cropBoxWidth = 0;
    let cropBoxHeight = 0;

    if (newWidth > newHeight * 2) {
      // Shrink width until it fits
      cropBoxWidth = Math.min(newWidth, wrapBoxWidth - margin * 2);
      cropBoxHeight = cropBoxWidth / 2;
  
      while (cropBoxWidth + margin * 2 > wrapBoxWidth || cropBoxHeight + margin * 2 > wrapBoxHeight) {
          newWidth *= 0.9;
          newHeight *= 0.9;
          cropBoxWidth = Math.min(newWidth, wrapBoxWidth - margin * 2);
          cropBoxHeight = cropBoxWidth / 2;
      }
  
      cropper.setCanvasData({
          width: newWidth,
          height: newHeight
      });
    } else if (newHeight >= newWidth) {
        // Shrink height until it fits
        cropBoxHeight = Math.min(newHeight, wrapBoxHeight - margin * 2);
        cropBoxWidth = cropBoxHeight * 2;
    
        while (cropBoxWidth + margin * 2 > wrapBoxWidth || cropBoxHeight + margin * 2 > wrapBoxHeight) {
            newWidth *= 0.9;
            newHeight *= 0.9;
            cropBoxHeight = Math.min(newHeight, wrapBoxHeight - margin * 2);
            cropBoxWidth = cropBoxHeight * 2;
        }
    
        cropper.setCanvasData({
            width: newWidth,
            height: newHeight
        });
    } else {
        cropBoxWidth = Math.min(newWidth, wrapBoxWidth - margin * 2);
        cropBoxHeight = cropBoxWidth / 2;
    }

    // Center the Image
    cropper.setCanvasData({
      left: Math.abs(wrapBoxWidth - newWidth) / 2,
      top: Math.abs(wrapBoxHeight - newHeight) / 2
    });

    // Set  and center crop box
    cropper.setCropBoxData({
      width: cropBoxWidth,
      height: cropBoxHeight,
      left: Math.abs(wrapBoxWidth - cropBoxWidth) / 2,
      top: Math.abs(wrapBoxHeight - cropBoxHeight) / 2
    });
  }

  onCreateImageUrl() {
    if (!this.imagesQueue.length) {
      this.currentImageUrl = '';
      return;
    }

    const windowURL = window.URL || window.webkitURL;

    this.currentImage = this.imagesQueue.shift();
    const url = windowURL.createObjectURL(this.currentImage);
    this.currentImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url) || '';
  }

  close(dialog, value) {
    this.dialogService.closeDialogSubject.next(value);

    const { initialState: { id, name, type, action, callback } } = dialog;
    if (value) {
      const croppedCanvas = this.image.cropper.getCroppedCanvas({
        fillColor: type === 'image/png' ? 'transparent': '#FFFFFF',
      });

      const dataUrl = croppedCanvas.toDataURL(type);
      this.imageService.imageDataUrlSubject.next({ id, result: dataUrl });

      croppedCanvas.toBlob(blob => {
        this.imageService.uploadImageByBlob(id, blob, name);
      }, type);
      if (action) {
        this.store.dispatch(action);
      }
      callback(value);
    }
  }

  onConfirm(dialog, value) {
    if (!this.currentImageUrl) {
      this.dialogService.closeDialogSubject.next(null);
      return;
    }
    this.isLoading = true;

    const { initialState: { action, callback } } = dialog;

    const croppedCanvas = this.image.cropper.getCroppedCanvas({
      fillColor: this.currentImage.type === 'image/png' ? 'transparent' : '#FFFFFF'
    });

    const dataUrl = croppedCanvas.toDataURL(this.currentImage.type);
    this.imageService.imageDataUrlSubject.next({ id: this.imageId, result: dataUrl, isMulti: true});

    croppedCanvas.toBlob(blob => {
      this.imageService.uploadImageByBlob( this.imageId, blob, this.currentImage.name, true);
    }, this.currentImage.type);

    if (action) {
      this.store.dispatch(action);
    }

    callback(value);
  }

  getCropperOptions(dialog) {
    const aspectRatio = _.get(dialog, 'initialState.aspectRatio', this.cropperOptions.aspectRatio);
    return  { ...this.cropperOptions,  aspectRatio };
  }

  zoomIn() {
    this.image.cropper.zoom(0.1);
  }

  zoomOut() {
    this.image.cropper.zoom(-0.1);
  }

  rotateLeft() {
    this.image.cropper.rotate(-90);
  }

  rotateRight() {
    this.image.cropper.rotate(90);
  }

}
